import { Box, Button, Grid, InputLabel, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { JobItem } from '../../../../../app/Slices/SevenDayApiTypes';
import { formatCurrency } from '../../../../../shared/utils';
import FormSelect from '../../../../shared/FormSelect';
import { PricingTemplate } from '../../../../types/contractor';
import {
    getItemBookingCutoffLabel,
    isPricingItemAvailableInBookingRange,
    isTemplateAvailableInBookingRange,
    JobBookingAvailability,
} from '../../../Contractors/utils/cutoff.utils';

const ReservationJobMenu = ({
    name,
    templates,
    onItemsChange,
    bookingAvailability,
}: {
    name: string;
    templates: PricingTemplate[];
    onItemsChange?: (items: JobItem[]) => void;
    bookingAvailability: JobBookingAvailability;
}) => {
    const methods = useFormContext();
    const { control, watch, setValue, trigger } = methods;
    const [expandedItem, setExpandedItem] = useState<number | null>(null);
    const collapseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    const clearCollapseTimeout = () => {
        if (collapseTimeoutRef.current) {
            clearTimeout(collapseTimeoutRef.current);
            collapseTimeoutRef.current = null;
        }
    };

    const scheduleCollapse = () => {
        clearCollapseTimeout();
        collapseTimeoutRef.current = setTimeout(() => {
            setExpandedItem(null);
            collapseTimeoutRef.current = null;
        }, 250);
    };

    useEffect(() => () => clearCollapseTimeout(), []);

    const isTemplateAvailable = (template: PricingTemplate) =>
        isTemplateAvailableInBookingRange({
            template,
            ...bookingAvailability,
        });

    const templateOptions = (templates || []).map((template) => {
        const isAvailable = isTemplateAvailable(template);

        return {
            value: template.id,
            label: isAvailable
                ? template.name
                : `${template.name} (unavailable)`,
            disabled: !isAvailable,
        };
    });

    useEffect(() => {
        if (!templates?.length) return;

        const currentTemplateId = watch(`${name}.templateId`);
        const currentTemplate = templates.find(
            (template) => template.id === currentTemplateId,
        );

        if (currentTemplate && isTemplateAvailable(currentTemplate)) return;

        const firstAvailable = templates.find(isTemplateAvailable);

        if (firstAvailable?.id != null) {
            setValue(`${name}.templateId`, firstAvailable.id);
        }
    }, [templates, bookingAvailability, name, setValue, watch]);

    const selectedTemplate = templates?.find(
        (template) => template.id === watch(`${name}.templateId`),
    );
    const currentItems = (watch(`${name}.items`) ?? []) as Array<JobItem>;

    const itemCutoff = (item?: { cutoff?: number | null }) => ({
        ...bookingAvailability,
        itemCutoff: item?.cutoff,
        templateCutoff: selectedTemplate?.cutoff,
    });

    const isItemAvailable = (item?: { cutoff?: number | null }) =>
        isPricingItemAvailableInBookingRange(itemCutoff(item));

    const handleExpandItem = (
        item: JobItem,
        templateItem?: { cutoff?: number | null },
    ) => {
        if (!isItemAvailable(templateItem)) return;

        setExpandedItem(item.pricingItem?.id ?? null);
        if (item.quantity === 0) {
            handleAddItem(item);
        }
    };

    const handleAddItem = (item: JobItem) => {
        setExpandedItem(item.pricingItem?.id ?? null);
        const newValues = currentItems.find(
            (i) =>
                i.pricingItem &&
                item.pricingItem &&
                i.pricingItem.id === item.pricingItem.id,
        )
            ? currentItems.map((i) =>
                  i.pricingItem &&
                  item.pricingItem &&
                  i.pricingItem.id === item.pricingItem.id
                      ? { ...i, quantity: i.quantity + 1 }
                      : i,
              )
            : [...currentItems, { ...item, quantity: 1 }];

        setValue(`${name}.items`, newValues, {
            shouldDirty: true,
            shouldValidate: true,
        });
        onItemsChange?.(newValues);
        trigger(`${name}.guestNotes`);
        trigger(`${name}.primaryActionField`);
    };

    const selectedTemplateId = selectedTemplate
        ? selectedTemplate.id
        : undefined;

    const deletedItems = currentItems.filter((i) => {
        return i.template.id === selectedTemplateId && !i.pricingItem;
    });

    const modifiedItems: JobItem[] =
        selectedTemplate && selectedTemplateId !== undefined
            ? selectedTemplate.items.map((item) => {
                  const foundItem = currentItems.find((i) => {
                      return i.pricingItem && i.pricingItem.id === item.id;
                  });

                  return {
                      name: item.name,
                      description: item.description,
                      cost: item.cost,
                      quantity: foundItem?.quantity ?? 0,
                      total: item.cost * (foundItem?.quantity ?? 0),
                      pricingItem: item.id ? { id: item.id } : undefined,
                      template: {
                          id: selectedTemplateId,
                          name: selectedTemplate.name,
                      },
                  };
              })
            : [];

    modifiedItems.push(...deletedItems);

    const handleDecrementItem = (item: JobItem) => {
        if (item.quantity <= 1) {
            const filtered = currentItems.filter((i) => {
                if (i.pricingItem && item.pricingItem) {
                    return i.pricingItem.id !== item.pricingItem.id;
                }
                return true;
            });
            setValue(`${name}.items`, filtered, {
                shouldDirty: true,
                shouldValidate: true,
            });
            onItemsChange?.(filtered);
            if (expandedItem === item.pricingItem?.id) setExpandedItem(null);
            return;
        } else {
            const newValues = currentItems.map((i) =>
                i.pricingItem &&
                item.pricingItem &&
                i.pricingItem.id === item.pricingItem.id
                    ? { ...i, quantity: i.quantity - 1 }
                    : i,
            );
            setValue(`${name}.items`, newValues, {
                shouldDirty: true,
                shouldValidate: true,
            });
            onItemsChange?.(newValues);
        }
    };

    return (
        <Grid
            container
            sx={{
                bgcolor: 'custom.cardActionBackground',
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'custom.grey',
            }}
        >
            <Grid item container xs={12} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                    <InputLabel
                        htmlFor={`${name}-template-input`}
                        id={`${name}-template-label`}
                    >
                        Price List
                    </InputLabel>
                </Grid>
                <Grid item xs={12}>
                    <FormSelect
                        id={`${name}-template-input`}
                        labelId={`${name}-template-label`}
                        name={`${name}.templateId`}
                        control={control}
                        options={templateOptions}
                    />
                </Grid>
            </Grid>
            <Box
                sx={{
                    maxHeight: 500,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    width: '100%',
                }}
            >
                <Grid container spacing={2}>
                    {modifiedItems.map((item: JobItem) => {
                        const costChanged = currentItems.find(
                            (i) =>
                                i.pricingItem?.id === item.pricingItem?.id &&
                                i.cost !== item.cost,
                        );
                        const nameChanged = currentItems.find(
                            (i) =>
                                i.pricingItem?.id === item.pricingItem?.id &&
                                i.name !== item.name,
                        );
                        const descriptionChanged = currentItems.find(
                            (i) =>
                                i.pricingItem?.id === item.pricingItem?.id &&
                                i.description !== item.description,
                        );
                        const templateItem = selectedTemplate?.items.find(
                            (pricingItem) =>
                                pricingItem.id === item.pricingItem?.id,
                        );
                        const itemAvailable = isItemAvailable(templateItem);
                        const cutoffLabel = getItemBookingCutoffLabel(
                            itemCutoff(templateItem),
                        );

                        return (
                            <Grid item xs={12} key={item.name}>
                                <Box
                                    role='group'
                                    aria-label={item.name}
                                    sx={{
                                        display: 'flex',
                                        p: 1.5,
                                        border: '1px solid',
                                        borderColor: 'custom.grey',
                                        borderRadius: 1,
                                        bgcolor: item.pricingItem
                                            ? 'primary.contrastText'
                                            : 'custom.lightGrey',
                                        flexDirection: 'column',
                                        opacity:
                                            item.pricingItem && !itemAvailable
                                                ? 0.6
                                                : 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: 0,
                                            pr: 2,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                color: item.pricingItem,
                                            }}
                                        >
                                            {item.name}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                            }}
                                        >
                                            {`${formatCurrency(
                                                item.cost,
                                            )} (ex. GST)`}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                            }}
                                        >
                                            {item.description}
                                        </Typography>
                                        {cutoffLabel && (
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    color: 'text.secondary',
                                                    mt: 0.5,
                                                }}
                                            >
                                                {cutoffLabel}
                                            </Typography>
                                        )}
                                        {item.pricingItem && !itemAvailable && (
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    color: 'error.main',
                                                    mt: 0.5,
                                                }}
                                            >
                                                Not available for the selected
                                                booking dates.
                                            </Typography>
                                        )}
                                        {!item.pricingItem && (
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    color: 'error.main',
                                                }}
                                            >
                                                This item has been deleted from
                                                the menu since you made your
                                                reservation. However, this won't
                                                affect your reservation and you
                                                can update the quantity in the
                                                selected items section.
                                            </Typography>
                                        )}
                                        {costChanged && (
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    color: 'error.main',
                                                }}
                                            >
                                                The price for this item has been
                                                changed since you made your
                                                reservation. However, this won't
                                                affect your reservation.
                                            </Typography>
                                        )}
                                        {nameChanged && (
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    color: 'error.main',
                                                }}
                                            >
                                                This item's name has been
                                                changed since you made your
                                                reservation. This won't affect
                                                your reservation.
                                            </Typography>
                                        )}
                                        {descriptionChanged && (
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    color: 'error.main',
                                                }}
                                            >
                                                This item's description has been
                                                changed since you made your
                                                reservation. However, this won't
                                                affect your reservation.
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box
                                        onMouseEnter={clearCollapseTimeout}
                                        onMouseLeave={scheduleCollapse}
                                        sx={{
                                            alignSelf: 'flex-end',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent:
                                                expandedItem ===
                                                item.pricingItem?.id
                                                    ? 'space-between'
                                                    : 'center',

                                            height: 30,
                                            width:
                                                expandedItem ===
                                                item.pricingItem?.id
                                                    ? 120
                                                    : 30,
                                            transition:
                                                'width 320ms cubic-bezier(0.4, 0, 0.2, 1)',
                                            overflow: 'hidden',
                                            border: '1px solid',
                                            borderColor: item.pricingItem
                                                ? 'primary.main'
                                                : 'text.primary',
                                            borderRadius:
                                                expandedItem ===
                                                item.pricingItem?.id
                                                    ? 5
                                                    : '50%',
                                            px:
                                                expandedItem ===
                                                item.pricingItem?.id
                                                    ? 1
                                                    : 0,
                                        }}
                                    >
                                        {expandedItem ===
                                        item.pricingItem?.id ? (
                                            <>
                                                <Button
                                                    variant='text'
                                                    size='small'
                                                    onClick={() =>
                                                        handleDecrementItem(
                                                            item,
                                                        )
                                                    }
                                                    sx={{
                                                        minWidth: 0,
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: '50%',
                                                        borderColor:
                                                            'custom.grey',
                                                        p: 0,
                                                    }}
                                                >
                                                    <Typography
                                                        fontWeight={700}
                                                    >
                                                        -
                                                    </Typography>
                                                </Button>
                                                <Typography
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {item.quantity}
                                                </Typography>
                                                <Button
                                                    variant='text'
                                                    size='small'
                                                    disabled={!itemAvailable}
                                                    onClick={() =>
                                                        handleAddItem(item)
                                                    }
                                                    sx={{
                                                        minWidth: 0,
                                                        width: 30,
                                                        height: 30,
                                                        borderRadius: '50%',
                                                        p: 0,
                                                    }}
                                                >
                                                    <Typography
                                                        fontWeight={700}
                                                    >
                                                        +
                                                    </Typography>
                                                </Button>
                                            </>
                                        ) : (
                                            <Button
                                                variant='text'
                                                size='small'
                                                disabled={
                                                    !item.pricingItem ||
                                                    (!itemAvailable &&
                                                        item.quantity === 0)
                                                }
                                                onClick={() =>
                                                    handleExpandItem(
                                                        item,
                                                        templateItem,
                                                    )
                                                }
                                                sx={{
                                                    minWidth: 0,
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: '50%',
                                                    p: 0,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        color: item.pricingItem
                                                            ? 'primary.main'
                                                            : 'text.primary',
                                                    }}
                                                    fontWeight={700}
                                                >
                                                    {item.quantity > 0
                                                        ? item.quantity
                                                        : '+'}
                                                </Typography>
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>
        </Grid>
    );
};

export default ReservationJobMenu;
